<script setup>
import { ref } from 'vue'
import { RouterLink } from 'vue-router'
import { useUserStore } from '../stores/userStore'
import { useNavBarSearch } from '../composables/useNavBarSearch'
import { useTheme } from '../composables/useTheme'
import { useAccentColor } from '../composables/useAccentColor'
import { useAdBlockDetector } from '../composables/useAdBlockDetector'
import MobileMenu from './nav/MobileMenu.vue'
import NavSearchBar from './nav/NavSearchBar.vue'
import ExploreDropdown from './nav/ExploreDropdown.vue'
import UserNavIcons from './nav/UserNavIcons.vue'
import AdBlockerModal from './AdBlockerModal.vue'

const { isDark, toggleTheme } = useTheme()
const { accentColor, accentColors, setAccentColor } = useAccentColor()
const { clearAdBlockDetection } = useAdBlockDetector()

const userStore = useUserStore()

const {
  showAdBlockModal,
  mobileMenuOpen,
  loginRedirect,
  handleLogoClick
} = useNavBarSearch()

const isMobileSearchOpen = ref(false)

const toggleMobileMenu = () => {
  if (isMobileSearchOpen.value) isMobileSearchOpen.value = false
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}

const closeMobileSearch = () => {
  isMobileSearchOpen.value = false
}
</script>

<template>
  <div>
    <AdBlockerModal
      v-if="showAdBlockModal"
      @close="showAdBlockModal = false"
      @continue="clearAdBlockDetection"
    />
    <nav
      class="bg-m3-surface/80 backdrop-blur-xl sticky top-0 z-50 border-b border-m3-outline-variant shadow-sm transition-colors duration-300"
    >
      <div class="container mx-auto px-4">
        <div class="flex justify-between items-center h-16">
          <!-- Logo (Hidden when mobile search is open) -->
          <RouterLink
            to="/"
            @click="handleLogoClick($event, closeMobileMenu)"
            v-show="!isMobileSearchOpen"
            class="text-xl lg:text-2xl font-bold text-brand-text tracking-tight flex-shrink-0 mr-2 md:mr-4 group sm:flex transition-transform duration-200 hover:scale-[1.02]"
          >
            <span class="text-brand-accent group-hover:text-brand-text transition-colors duration-300">SendThe</span><span class="text-brand-text hidden sm:inline">Noise</span>
            <span class="text-brand-text sm:hidden">Noise</span>
          </RouterLink>

          <!-- Mobile Search Trigger -->
          <button
            @click="isMobileSearchOpen = true"
            v-show="!isMobileSearchOpen"
            class="sm:hidden p-2 text-brand-muted hover:text-brand-accent transition-colors ml-auto mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            :aria-label="$t('nav.open_search')"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>

          <!-- Search Bar -->
          <NavSearchBar :is-mobile-open="isMobileSearchOpen" @close-mobile-search="closeMobileSearch" />

          <!-- Desktop Explore Navigation -->
          <ExploreDropdown />

          <!-- Desktop User Navigation -->
          <UserNavIcons
            :is-logged-in="userStore.isLoggedIn"
            :is-admin="userStore.isAdmin"
            :login-redirect="loginRedirect"
            :accent-colors="accentColors"
            :accent-color="accentColor"
            :is-dark="isDark"
            @toggle-theme="toggleTheme"
            @set-accent-color="(key) => setAccentColor(key, isDark)"
          />

          <!-- Mobile/Tablet User Profile (Visible < lg) - HMR Trigger -->
          <div class="lg:hidden flex items-center mr-1" v-show="!isMobileSearchOpen">
            <template v-if="userStore.isLoggedIn">
              <RouterLink
                to="/profile"
                class="p-2 text-brand-text hover:text-brand-accent transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-brand-muted/10 group"
                :aria-label="$t('nav.profile')"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 group-hover:scale-110 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </RouterLink>
            </template>
            <template v-else>
              <RouterLink
                :to="loginRedirect"
                class="p-2 text-brand-text hover:text-brand-accent transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-brand-muted/10 group"
                :aria-label="$t('nav.login')"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 group-hover:scale-110 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
              </RouterLink>
            </template>
          </div>

          <!-- Mobile Menu Button -->
          <button
            @click="toggleMobileMenu"
            v-show="!isMobileSearchOpen"
            class="lg:hidden p-2 rounded-lg text-brand-text hover:text-brand-accent hover:bg-brand-muted/10 transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center z-[60]"
            :aria-label="mobileMenuOpen ? $t('nav.close_menu_aria') : $t('nav.main_menu_aria')"
            :aria-expanded="mobileMenuOpen"
          >
            <svg v-if="!mobileMenuOpen" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </nav>

    <!-- Mobile Menu Drawer -->
    <MobileMenu
      :is-open="mobileMenuOpen"
      :is-logged-in="userStore.isLoggedIn"
      :is-admin="userStore.isAdmin"
      :username="userStore.currentUser?.username || userStore.currentUser?.email?.split('@')[0]"
      :accent-colors="accentColors"
      :accent-color="accentColor"
      :is-dark="isDark"
      @close="mobileMenuOpen = false"
      @toggle-theme="toggleTheme"
      @set-accent-color="(key) => setAccentColor(key, isDark)"
    />
  </div>
</template>